import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import EmployeeWithShift from './EmployeeWithShift';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';

import { Save } from '@mui/icons-material';
import { masterApi } from '../../../api/masterApi';
import { shiftApi } from '../../../api/shiftApi';
import { employeeApi } from '../../../api/employeeApi';
import { DropdownItem, Employee } from '../../../types';
import { toast } from 'react-toastify';

type SelectAllOption = {
  id: '__select_all__';
  fullName: string;
  employeeCode: string;
  isSelectAll: true;
};

type EmployeeOption = Employee | SelectAllOption;

const selectAllOption: SelectAllOption = {
  id: '__select_all__',
  fullName: 'Select All Employees',
  employeeCode: '',
  isSelectAll: true,
};

const employeeFilterOptions = createFilterOptions<EmployeeOption>({
  stringify: (option) =>
    'isSelectAll' in option ? option.fullName : `${option.fullName} ${option.employeeCode}`,
});

const getEmployeeLabel = (employee: Employee) => `${employee.fullName} (${employee.employeeCode})`;

const EmployeeMapping: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [shifts, setShifts] = useState<DropdownItem[]>([]);
  const [workWeeks, setWorkWeeks] = useState<DropdownItem[]>([]);
  const [department, setDepartment] = useState('');
  const [shift, setShift] = useState('');
  const [workWeek, setWorkWeek] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [refreshListKey, setRefreshListKey] = useState(0);

  // Fetch master data
  useEffect(() => {
    masterApi.getDepartments().then(setDepartments).catch(() => {});
    shiftApi
      .getShifts()
      .then((items) => setShifts(items.map((s: any) => ({ id: s.id, name: s.shiftName }))))
      .catch(() => {});
    shiftApi
      .getWorkWeekOptions()
      .then((items) => setWorkWeeks(items.map((w: any) => ({ id: w.id, name: w.name }))))
      .catch(() => {});
  }, []);

  // Fetch employees with debounce
  useEffect(() => {
    let isCancelled = false;

    const timeoutId = window.setTimeout(async () => {
      setEmployeeLoading(true);
      try {
        const result = await employeeApi.getAll(
          1,
          50,
          employeeSearchText.trim() || undefined,
          { departmentId: department || undefined }
        );
        if (!isCancelled) setEmployees(result);
      } catch {
        if (!isCancelled) setEmployees([]);
      } finally {
        if (!isCancelled) setEmployeeLoading(false);
      }
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [department, employeeSearchText]);

  // Clear selection when department changes (single mode only)
  useEffect(() => {
    if (!department) return;

    if (!bulkMode) {
      if (selectedEmployee && selectedEmployee.departmentId !== department) {
        setSelectedEmployee(null);
        setEmployeeSearchText('');
      }
    }
    // Bulk mode: keep cross-department selections intact
  }, [department, bulkMode]);

  const employeeSelectionOptions = useMemo(() => {
    if (!selectedEmployees.length) return employees;

    return [
      ...selectedEmployees,
      ...employees.filter((emp) => !selectedEmployees.some((sel) => sel.id === emp.id)),
    ];
  }, [employees, selectedEmployees]);

  const isAllSelected =
    employeeSelectionOptions.length > 0 &&
    employeeSelectionOptions.every((emp) => selectedEmployees.some((sel) => sel.id === emp.id));

  const employeeOptions = useMemo(() => {
    if (!bulkMode) return employeeSelectionOptions;

    if (!employeeSelectionOptions.length) return [];

    return [selectAllOption, ...employeeSelectionOptions] as EmployeeOption[];
  }, [employeeSelectionOptions, bulkMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !shift ||
      !workWeek ||
      !effectiveDate ||
      (!bulkMode && !selectedEmployee) ||
      (bulkMode && !selectedEmployees.length)
    ) {
      toast.error('Please select required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (bulkMode) {
        await Promise.all(
          selectedEmployees.map((emp) =>
            Promise.all([
              shiftApi.mapEmployeeToShift({
                employeeId: emp.id,
                shiftId: shift,
                departmentId: department || undefined,
                effectiveDate,
              }),
              shiftApi.mapEmployeeToWorkWeek({
                employeeId: emp.id,
                workWeekId: workWeek,
                effectiveDate,
              }),
            ])
          )
        );
        toast.success('Employee(s) mapped to shift and work week successfully!');
      } else if (selectedEmployee) {
        await Promise.all([
          shiftApi.mapEmployeeToShift({
            employeeId: selectedEmployee.id,
            shiftId: shift,
            departmentId: department || undefined,
            effectiveDate,
          }),
          shiftApi.mapEmployeeToWorkWeek({
            employeeId: selectedEmployee.id,
            workWeekId: workWeek,
            effectiveDate,
          }),
        ]);
        toast.success('Employee mapped to shift and work week successfully!');
      }

      // Reset form
      setSelectedEmployees([]);
      setSelectedEmployee(null);
      setEmployeeSearchText('');
      setShift('');
      setWorkWeek('');
      setEffectiveDate(new Date().toISOString().split('T')[0]);
      setModalOpen(false);
      setRefreshListKey((k) => k + 1);
    } catch (err: any) {
      const responseData = err.response?.data;
      toast.error(responseData?.details || responseData?.message || 'Failed to map employee(s)');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Employee Mapping
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Assign employees to shifts by department. Use the switch to choose between single
          employee selection or multiple employees with checkboxes.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
          sx={{ mb: 3, textTransform: 'none' }}
        >
          Add Mapping
        </Button>

        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Employee Mapping</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={department}
                      label="Department"
                      onChange={(e) => setDepartment(e.target.value)}
                    >
                      <MenuItem value="">All Departments</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bulkMode}
                        onChange={(_, checked) => {
                          setBulkMode(checked);
                          setSelectedEmployees([]);
                          setSelectedEmployee(null);
                          setEmployeeSearchText('');
                        }}
                      />
                    }
                    label="Bulk Mapping (All Employees)"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  {bulkMode ? (
                    <Autocomplete<EmployeeOption, true, false, false>
                      multiple
                      disableCloseOnSelect
                      size="small"
                      options={employeeOptions}
                      value={selectedEmployees}
                      inputValue={employeeSearchText}
                      loading={employeeLoading}
                      filterOptions={(options, params) => {
                        const filtered = employeeFilterOptions(options, params);
                        if (options.some((option) => 'isSelectAll' in option)) {
                          const selectAll = options.find(
                            (option): option is SelectAllOption => 'isSelectAll' in option
                          )!;
                          return [
                            selectAll,
                            ...filtered.filter((option) => !('isSelectAll' in option)),
                          ];
                        }
                        return filtered;
                      }}
                      onChange={(_, newValue) => {
                        const hasSelectAll = newValue.some((item) => 'isSelectAll' in item);
                        if (hasSelectAll) {
                          if (isAllSelected) {
                            setSelectedEmployees([]);
                          } else {
                            setSelectedEmployees(employeeSelectionOptions);
                          }
                          setEmployeeSearchText('');
                          return;
                        }
                        setSelectedEmployees(newValue as Employee[]);
                        setEmployeeSearchText('');
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') setEmployeeSearchText(newInputValue);
                        if (reason === 'clear') {
                          setSelectedEmployees([]);
                          setEmployeeSearchText('');
                        }
                      }}
                      getOptionLabel={(option) =>
                        'isSelectAll' in option ? option.fullName : getEmployeeLabel(option)
                      }
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      noOptionsText="No employees found"
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            style={{ marginRight: 8 }}
                            checked={
                              'isSelectAll' in option ? isAllSelected : selected
                            }
                          />
                          {'isSelectAll' in option
                            ? option.fullName
                            : getEmployeeLabel(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employees *"
                          placeholder="Search employee by name or code"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {employeeLoading ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          alignItems: 'flex-start',
                          minHeight: 40,
                        },
                      }}
                    />
                  ) : (
                    <Autocomplete<Employee, false, false, false>
                      size="small"
                      options={employees}
                      value={selectedEmployee}
                      inputValue={employeeSearchText}
                      loading={employeeLoading}
                      onChange={(_, newValue) => {
                        setSelectedEmployee(newValue);
                        setEmployeeSearchText(newValue ? getEmployeeLabel(newValue) : '');
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') setEmployeeSearchText(newInputValue);
                        if (reason === 'clear') {
                          setSelectedEmployee(null);
                          setEmployeeSearchText('');
                        }
                      }}
                      getOptionLabel={(option) => getEmployeeLabel(option)}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      noOptionsText="No employees found"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employee *"
                          placeholder="Search employee by name or code"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {employeeLoading ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Work Week *</InputLabel>
                    <Select
                      value={workWeek}
                      label="Work Week *"
                      onChange={(e) => setWorkWeek(e.target.value)}
                    >
                      <MenuItem value="">Select Work Week</MenuItem>
                      {workWeeks.map((w) => (
                        <MenuItem key={w.id} value={w.id}>
                          {w.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Shift *</InputLabel>
                    <Select
                      value={shift}
                      label="Shift *"
                      onChange={(e) => setShift(e.target.value)}
                    >
                      <MenuItem value="">Select Shift</MenuItem>
                      {shifts.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="Effective Date *"
                    type="date"
                    fullWidth
                    size="small"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={() => setModalOpen(false)} color="secondary" variant="outlined" sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={
                    submitting ? <CircularProgress size={18} color="inherit" /> : <Save />
                  }
                  disabled={
                    submitting ||
                    !shift ||
                    !workWeek ||
                    !effectiveDate ||
                    (!bulkMode && !selectedEmployee) ||
                    (bulkMode && !selectedEmployees.length)
                  }
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? 'Saving...' : bulkMode ? 'Bulk Map' : 'Save Mapping'}
                </Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>

        <Box sx={{ mt: 4 }}>
          <EmployeeWithShift key={refreshListKey} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeeMapping;
